/**
 * Chat API client for n8n endpoints
 */
export class ChatAPI {
    constructor(endpointId) {
        this.endpointId = endpointId;
        this.baseUrl = `https://automate.prozesshub.eu/webhook/${endpointId}/chat`;
    }

    /**
     * Get the session ID for this endpoint
     * @returns {string} The session ID
     */
    async getSessionId() {
        // Dynamic import to avoid circular dependencies
        const { getSessionId } = await import('./storage.js');
        return getSessionId(this.endpointId);
    }

    /**
     * Send a message to the chat endpoint
     * @param {string} message - The message to send
     * @returns {Promise<Object>} The API response
     */
    async sendMessage(message) {
        try {
            const sessionId = await this.getSessionId();
            const response = await fetch(this.baseUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'sendMessage',
                    sessionId: sessionId,
                    chatInput: message
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('API Raw Response:', data);
            
            // Import content formatter for consistency
            const { formatContent } = await import('./contentFormatter.js');
            
            // Handle new API schema with output.answer and output.suggestions
            if (data.output) {
                console.log('data.output.answer:', data.output.answer);
                console.log('data.output.answer type:', typeof data.output.answer);
                console.log('data.output.suggestions:', data.output.suggestions);
                
                const formattedAnswer = formatContent(data.output.answer);
                
                const result = {
                    answer: formattedAnswer,
                    suggestions: Array.isArray(data.output.suggestions) ? data.output.suggestions : []
                };
                console.log('API Result:', result);
                return result;
            }
            
            // Fallback for other response formats
            const fallbackContent = data.message || data.output || data.response || data.text;
            return {
                answer: formatContent(fallbackContent),
                suggestions: []
            };
        } catch (error) {
            console.error('API Error:', error);
            throw new Error(`Failed to send message: ${error.message}`);
        }
    }

    /**
     * Test if the endpoint is reachable
     * @returns {Promise<boolean>} True if endpoint is reachable
     */
    async testEndpoint() {
        try {
            const response = await fetch(this.baseUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'ping',
                    sessionId: this.endpointId,
                    chatInput: 'test'
                })
            });
            
            return response.ok;
        } catch (error) {
            console.error('Endpoint test failed:', error);
            return false;
        }
    }
}

/**
 * Create a new ChatAPI instance for the given endpoint
 * @param {string} endpointId - The endpoint ID
 * @returns {ChatAPI} The API instance
 */
export function createChatAPI(endpointId) {
    return new ChatAPI(endpointId);
}
